import { FindStorePosProductResponseDto } from '@orderna/admin-panel/src/app/core/stores/dtos/partials/find-store-pos-product.response.dto';
import { ApplicableDiscount } from '@orderna/admin-panel/src/app/model/discount-applicable';
import { DiscountType } from '@orderna/admin-panel/src/app/model/enum/discount-type';
import { TAX_RATE } from '@orderna/admin-panel/src/app/common/constants/tax-rate.constant';
import { FindCustomerNamesResponseDto } from '../../../customers/dtos/find-customer-names.response.dto';
import { FindCustomerStatutoryDiscountsResponseDto } from '../../../customers/dtos/find-customer-statutory-discounts.response.dto';
import { ProductTaxType } from '../../../products/enums/product-tax-type.enum';
import { buildCurrentTransactionItemKey } from '../utils/build-current-transaction-item-key';
import { StoreType } from '@orderna/admin-panel/src/app/core/stores/enums/store-type.enum';

export type CurrentTransactionStore = {
  isTaxable: boolean;
  type: StoreType;
  serviceChargeRate?: number;
};

export type CurrentTransactionProduct = Omit<
  FindStorePosProductResponseDto,
  'modifiers' | 'bundles'
> & {
  /**
   * Selected modifier options for regular products.
   * Mutually exclusive with selectedBundleOptions.
   */
  selectedModifierOptions?: CurrentTransactionModifierOption[];

  /**
   * Selected products for bundle products.
   * Bundle items may have their own modifier options.
   * Mutually exclusive with selectedModifierOptions.
   */
  selectedBundleOptions?: CurrentTransactionBundleOption[];
};

export interface CurrentTransactionModifierOption {
  id: number;
  title: string;
  priceAdjustment: number;
  displayOrder: number;
}

export interface CurrentTransactionBundleOption {
  id: number;
  productId: number;
  productTitle: string;
  priceAdjustment: number;
  quantity: number;

  selectedModifierOptions: CurrentTransactionModifierOption[];
}

export type CurrentTransactionQuantity = number;

export type CurrentTransactionCustomer =
  | FindCustomerNamesResponseDto['data'][0]
  | null;
export type CurrentTransactionNote = string | null;
export type CurrentTransactionDiscountMode = 'statutory' | 'promo';

export type CurrentTransactionPromoItemDiscount = ApplicableDiscount | null;
export type CurrentTransactionPromoTransactionDiscount =
  ApplicableDiscount | null;
export type CurrentTransactionStatutoryDiscount =
  | (FindCustomerStatutoryDiscountsResponseDto[0] & {
      maxDiscountBase?: number;
    })
  | null;
export type CurrentTransactionCustomerStatutoryDiscount =
  FindCustomerStatutoryDiscountsResponseDto[0] & {
    maxDiscountBase?: number;
  };

export type CurrentTransactionSerializedItem = {
  store: CurrentTransactionStore;
  product: CurrentTransactionProduct;
  quantity: CurrentTransactionQuantity;
  note: CurrentTransactionNote;
  promoItemDiscount: CurrentTransactionPromoItemDiscount;
  promoTransactionDiscount: CurrentTransactionPromoTransactionDiscount;
  statutoryDiscount: CurrentTransactionStatutoryDiscount;
  discountMode: CurrentTransactionDiscountMode;
};
export type CurrentTransactionSerializedItemEntry = [
  string,
  CurrentTransactionSerializedItem,
];

export class CurrentTransactionItem {
  #store: CurrentTransactionStore;

  #product: CurrentTransactionProduct;

  #quantity: CurrentTransactionQuantity;
  #note: CurrentTransactionNote;

  #promoItemDiscount: CurrentTransactionPromoItemDiscount;
  #promoTransactionDiscount: CurrentTransactionPromoTransactionDiscount;
  #statutoryDiscount: CurrentTransactionStatutoryDiscount;
  #discountMode: CurrentTransactionDiscountMode;

  constructor(
    store: CurrentTransactionStore,
    product: CurrentTransactionProduct,
    quantity: CurrentTransactionQuantity,
    note: CurrentTransactionNote = null,
    promoItemDiscount: CurrentTransactionPromoItemDiscount = null,
    promoTransactionDiscount: CurrentTransactionPromoTransactionDiscount = null,
    statutoryDiscount: CurrentTransactionStatutoryDiscount = null,
    discountMode: CurrentTransactionDiscountMode = 'promo',
  ) {
    this.#store = store;

    this.#product = product;
    this.#quantity = quantity;
    this.#note = note;

    this.#promoItemDiscount = promoItemDiscount;
    this.#promoTransactionDiscount = promoTransactionDiscount;
    this.#statutoryDiscount = statutoryDiscount;
    this.#discountMode = discountMode;
  }

  get key(): string {
    return buildCurrentTransactionItemKey(this.#product);
  }

  get title(): string {
    const title = this.#product.title;

    const selectedModifierOptions = this.#product.selectedModifierOptions ?? [];

    if (selectedModifierOptions.length === 0) {
      return title;
    }

    const options = selectedModifierOptions
      .slice() // avoid mutating the original array
      .sort((a, b) => a.displayOrder - b.displayOrder)
      .map((option) => option.title)
      .join(', ');

    return `${title} (${options})`;
  }

  get bundleOptions() {
    return (this.#product.selectedBundleOptions ?? []).map((item) => ({
      qty: item.quantity,
      title: this.buildBundleOptionTitle(item),
    }));
  }

  // Discount Mode
  get discountMode() {
    return this.#discountMode;
  }

  set discountMode(value: CurrentTransactionDiscountMode) {
    this.#discountMode = value;
  }

  // Product
  get product() {
    return this.#product;
  }

  // Quantity
  get quantity() {
    return this.#quantity;
  }

  set quantity(value: CurrentTransactionQuantity) {
    this.#quantity = value;
  }

  // Price
  get price() {
    const modifierPrice = this.getModifierPrice(
      this.#product.selectedModifierOptions,
    );

    const bundleOptionPrice = (this.#product.selectedBundleOptions ?? []).reduce(
      (total, bundleOption) => {
        const modifierPrice = this.getModifierPrice(
          bundleOption.selectedModifierOptions,
        );

        const unitPrice = bundleOption.priceAdjustment + modifierPrice;

        return total + unitPrice * bundleOption.quantity;
      },
      0,
    );

    return this.#product.price + modifierPrice + bundleOptionPrice;
  }

  // Note
  get note() {
    return this.#note;
  }

  set note(value: CurrentTransactionNote) {
    this.#note = value;
  }

  // Tax Type
  get taxType() {
    return this.#product.taxType;
  }

  get taxTypeMarker() {
    switch (this.#product.taxType) {
      case ProductTaxType.VATABLE:
        return 'V';
      case ProductTaxType.VAT_EXEMPT:
        return 'E';
      case ProductTaxType.ZERO_RATED:
        return 'Z';
    }
  }

  get taxTypeMarkerTooltipText() {
    return `
      VAT: ${this.tax.toFixed(2)}
      VATable: ${this.taxable.toFixed(2)}
      VAT Exempted: ${this.taxExempted.toFixed(2)}
      VAT Removed: ${this.isStatutoryDiscountApplied ? this.lessStatutoryTaxExempted.toFixed(2) : 0}
    `;
  }

  // Promo Discount
  set promoItemDiscount(value: CurrentTransactionPromoItemDiscount) {
    this.#promoItemDiscount = value;
  }

  get promoItemDiscount() {
    return this.#promoItemDiscount;
  }

  set promoTransactionDiscount(
    value: CurrentTransactionPromoTransactionDiscount,
  ) {
    this.#promoTransactionDiscount = value;
  }

  get promoTransactionDiscount() {
    return this.#promoTransactionDiscount;
  }

  // Statutory Discount
  set statutoryDiscount(value: CurrentTransactionStatutoryDiscount) {
    this.#statutoryDiscount = value;
  }

  get statutoryDiscount() {
    return this.#statutoryDiscount;
  }

  get isStoreTaxable(): boolean {
    return this.#store.isTaxable;
  }

  get isProductTaxable(): boolean {
    return this.#product.taxType === ProductTaxType.VATABLE;
  }

  get isTaxable(): boolean {
    return this.isStoreTaxable && this.isProductTaxable;
  }

  get isPromoDiscountApplied() {
    return this.#discountMode === 'promo';
  }

  get isStatutoryDiscountApplied() {
    return this.#discountMode === 'statutory';
  }

  get isStatutoryDiscountTaxExempt(): boolean {
    return (
      this.isStatutoryDiscountApplied && this.statutoryDiscount!.isVatExempt
    );
  }

  get subtotal(): number {
    return this.price * this.quantity;
  }

  get subtotalLessItemDiscount(): number {
    return this.subtotal - this.lessItemDiscount;
  }

  get lessStatutoryTaxExempted(): number {
    const discount = this.#statutoryDiscount;
    if (!discount || !discount.isVatExempt) return 0;
    return this.isTaxable
      ? this.subtotal - this.subtotal / (1 + TAX_RATE.PH)
      : 0;
  }

  get lessStatutoryDiscount(): number {
    const discount = this.#statutoryDiscount;
    if (!discount) return 0;
    let discountBase = this.subtotal - this.lessStatutoryTaxExempted;
    if (typeof discount.maxDiscountBase === 'number') {
      discountBase = Math.min(discountBase, discount.maxDiscountBase);
    }
    return discountBase * discount.discountRate;
  }

  get lessItemDiscount(): number {
    const discount = this.#promoItemDiscount;
    if (!discount) return 0;
    const discountedAmount =
      discount.type === DiscountType.FIXED
        ? Math.min(this.price, discount.value)
        : this.price * (discount.value / 100);
    return discountedAmount * this.quantity;
  }

  get lessTransactionDiscount(): number {
    const discount = this.#promoTransactionDiscount;
    if (!discount) return 0;
    return discount.discountedAmount;
  }

  get lessPromoDiscount(): number {
    return this.lessItemDiscount + this.lessTransactionDiscount;
  }

  get subtotalAfterItemDiscount(): number {
    return this.subtotal - this.lessItemDiscount;
  }

  get netOfTax(): number {
    if (this.isPromoDiscountApplied) {
      let base = this.subtotal - this.lessPromoDiscount;
      return this.isTaxable ? base / (1 + TAX_RATE.PH) : base;
    } else if (this.isStatutoryDiscountTaxExempt) {
      // e.g. SC/PWD/SP
      return this.subtotal - this.lessStatutoryTaxExempted;
    } else {
      // e.g. NAAC/MOV/BNPC
      let base = this.subtotal;
      return this.isTaxable ? base / (1 + TAX_RATE.PH) : base;
    }
  }

  get taxable(): number {
    return this.isTaxable && !this.isStatutoryDiscountTaxExempt
      ? this.netOfTax
      : 0;
  }

  get tax(): number {
    return this.taxable * TAX_RATE.PH;
  }

  get taxExempted(): number {
    return !this.isTaxable || this.isStatutoryDiscountTaxExempt
      ? this.netOfTax
      : 0;
  }

  get zeroRated(): number {
    return 0;
  }

  get serviceChargeRate(): number {
    return this.#store.serviceChargeRate ?? 0;
  }

  get serviceChargeAmount(): number {
    const rate = this.serviceChargeRate;
    if (rate === 0) return 0;

    // Service charge is applied on the amount after ALL discounts
    let base = this.subtotal;

    // Ensure base is not negative
    if (base < 0) base = 0;

    return base * (rate / 100);
  }

  get total(): number {
    let total = this.netOfTax + this.tax;
    if (this.isStatutoryDiscountApplied) {
      total -= this.lessStatutoryDiscount;
    }
    total += this.serviceChargeAmount;
    return total;
  }

  toSerializable(): CurrentTransactionSerializedItem {
    return {
      store: this.#store,
      product: this.#product,
      quantity: this.#quantity,
      note: this.#note,
      promoItemDiscount: this.#promoItemDiscount,
      promoTransactionDiscount: this.#promoTransactionDiscount,
      statutoryDiscount: this.#statutoryDiscount,
      discountMode: this.#discountMode,
    };
  }

  static fromSerializable(
    item: CurrentTransactionSerializedItem,
  ): CurrentTransactionItem {
    return new CurrentTransactionItem(
      item.store,
      item.product,
      item.quantity,
      item.note,
      item.promoItemDiscount,
      item.promoTransactionDiscount,
      item.statutoryDiscount,
      item.discountMode,
    );
  }

  clone() {
    return new CurrentTransactionItem(
      this.#store,
      this.#product,
      this.#quantity,
      this.#note,
      this.#promoItemDiscount,
      this.#promoTransactionDiscount,
      this.#statutoryDiscount,
      this.#discountMode,
    );
  }

  private buildBundleOptionTitle(item: CurrentTransactionBundleOption): string {
    const modifierOptions = item.selectedModifierOptions
      .slice()
      .sort((a, b) => a.displayOrder - b.displayOrder)
      .map((option) => option.title)
      .join(', ');

    return modifierOptions
      ? `${item.productTitle} (${modifierOptions})`
      : item.productTitle;
  }

  private getModifierPrice(
    options: { priceAdjustment: number }[] | null | undefined,
  ): number {
    return (
      options?.reduce((total, option) => total + option.priceAdjustment, 0) ?? 0
    );
  }
}
